import { useState } from 'react';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  Checkbox,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  ScrollArea,
  cn,
} from '@design-system';

import { Check, ChevronDown, Clock, MoreVertical, Search, Share2, Trash2 } from 'lucide-react';

import type { API } from '@/api/types';
import { PartnerInviteAcknowledgeDialog } from './partner-invite-acknowledge-dialog';

type DistributionPartnerSummary = API.DistributionPartnerSummary;
type DistributionContactWithPartner = API.DistributionPartnerContact & { partnerKey: string };

interface PartnerConnectionFormProps {
  partners: DistributionPartnerSummary[];
  isLoadingPartners: boolean;
  contacts: DistributionContactWithPartner[];
  isLoadingContacts: boolean;
  selectedPartnerKeys: string[];
  selectedContactIds: string[];
  onTogglePartner: (partnerKey: string) => void;
  onToggleContact: (advisorId: string, checked: boolean) => void;
  onToggleContacts: (advisorIds: string[], checked: boolean) => void;
  onConnect: () => void;
  isConnecting: boolean;
  onRequestOther: (
    name: string,
    email: string,
    organizationName: string,
    onSuccess?: () => void,
  ) => void;
  isRequestingOther: boolean;
  // Returns true when the email already belongs to a Waterlily account (invite is blocked).
  onCheckContactExists: (email: string) => Promise<boolean>;
  isCheckingContact: boolean;
  onRequestDisconnect: (contact: DistributionContactWithPartner) => void;
  onSetContactSharing: (contact: DistributionContactWithPartner, autoShare: boolean) => void;
  isSettingSharing: boolean;
  onShareContactLegacy: (contact: DistributionContactWithPartner, share: boolean) => void;
  isSharingContactLegacy: boolean;
  // EDJ advisors don't share earlier clients, so the legacy badge/action is hidden for them.
  showLegacyShare?: boolean;
  // Hides the "Don't see who you work with?" off-list invite card (e.g. when the form is embedded
  // in the questionnaire, where we only want partner + contact selection).
  hideInvite?: boolean;
  // Frames the section labels as a forward-looking choice ("who you want to work with") rather than
  // an existing relationship ("who you work with") — used in the onboarding questionnaire.
  prospective?: boolean;
  // Reframed EDJ election mode: sharing is managed purely per-client via Share with Supervisor, so
  // the per-contact auto-share toggle and its "sharing" badges are hidden in favor of a neutral
  // connected state and factual invite copy.
  edjMode?: boolean;
  disabled?: boolean;
  idPrefix?: string;
}

function contactDisplayName(contact: DistributionContactWithPartner): string {
  const name = `${contact.advisorFirstName ?? ''} ${contact.advisorLastName ?? ''}`.trim();

  return name || contact.advisorEmail;
}

// Once the partner list grows past a few, the logo grid gets unwieldy — switch to a searchable
// multi-select dropdown with logos instead.
const PARTNER_GRID_MAX = 3;

// Marks a self-service partner the advisor created via an off-list invite that's still awaiting
// Waterlily review — it's visible only to them until approved.
function PendingPartnerBadge() {
  return (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
      Pending review
    </span>
  );
}

interface PartnerMultiSelectProps {
  partners: DistributionPartnerSummary[];
  selectedPartnerKeys: string[];
  onTogglePartner: (partnerKey: string) => void;
  disabled?: boolean;
}

function PartnerMultiSelect({
  partners,
  selectedPartnerKeys,
  onTogglePartner,
  disabled,
}: PartnerMultiSelectProps) {
  const selectedNames = partners
    .filter(partner => selectedPartnerKeys.includes(partner.partnerKey))
    .map(partner => partner.displayName);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selectedNames.length > 0
              ? selectedNames.join(', ')
              : 'Select your distribution partners'}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-(--radix-dropdown-menu-trigger-width) max-h-80 overflow-y-auto"
      >
        {partners.map(partner => (
          <DropdownMenuCheckboxItem
            key={partner.partnerKey}
            checked={selectedPartnerKeys.includes(partner.partnerKey)}
            // Keep the menu open so several partners can be toggled in one pass.
            onSelect={event => event.preventDefault()}
            onCheckedChange={() => onTogglePartner(partner.partnerKey)}
            className="gap-2"
          >
            {partner.logoUrl && (
              <img
                src={partner.logoUrl}
                alt=""
                className="h-5 max-w-16 object-contain"
              />
            )}
            <span>{partner.displayName}</span>
            {partner.approvalStatus === 'pending' && <PendingPartnerBadge />}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PartnerConnectionForm({
  partners,
  isLoadingPartners,
  contacts,
  isLoadingContacts,
  selectedPartnerKeys,
  selectedContactIds,
  onTogglePartner,
  onToggleContact,
  onToggleContacts,
  onConnect,
  isConnecting,
  onRequestOther,
  isRequestingOther,
  onCheckContactExists,
  isCheckingContact,
  onRequestDisconnect,
  onSetContactSharing,
  isSettingSharing,
  onShareContactLegacy,
  isSharingContactLegacy,
  showLegacyShare = true,
  edjMode = false,
  hideInvite = false,
  prospective = false,
  disabled = false,
  idPrefix = '',
}: PartnerConnectionFormProps) {
  // Only enable "Connect" when a not-yet-connected contact is selected. Re-connecting contacts
  // that are already connected would re-run the connect/auto-share call for no change.
  const hasNewContactSelection = contacts.some(
    contact => selectedContactIds.includes(contact.advisorId) && !contact.isConnected,
  );
  const hasPartnerSelection = selectedPartnerKeys.length > 0;
  const selectedPartners = partners.filter(partner =>
    selectedPartnerKeys.includes(partner.partnerKey),
  );

  const [contactSearch, setContactSearch] = useState('');
  const normalizedSearch = contactSearch.trim().toLowerCase();
  const filteredContacts = normalizedSearch
    ? contacts.filter(contact =>
        `${contact.advisorFirstName ?? ''} ${contact.advisorLastName ?? ''} ${contact.advisorEmail}`
          .toLowerCase()
          .includes(normalizedSearch),
      )
    : contacts;

  const allFilteredSelected =
    filteredContacts.length > 0 &&
    filteredContacts.every(contact => selectedContactIds.includes(contact.advisorId));

  // Surface the contacts the advisor is connected with (pre-selected) at the top so they don't
  // have to scroll to find them. Stable partition keeps each group's original order.
  const orderedContacts = [
    ...filteredContacts.filter(contact => selectedContactIds.includes(contact.advisorId)),
    ...filteredContacts.filter(contact => !selectedContactIds.includes(contact.advisorId)),
  ];

  const [otherName, setOtherName] = useState('');
  const [otherEmail, setOtherEmail] = useState('');
  const [otherOrg, setOtherOrg] = useState('');
  const [inviteAckOpen, setInviteAckOpen] = useState(false);
  const otherEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(otherEmail.trim());
  const canInvite = otherName.trim().length > 0 && otherEmailValid;

  const sendInvite = () =>
    onRequestOther(otherName, otherEmail.trim(), otherOrg, () => {
      setOtherName('');
      setOtherEmail('');
      setOtherOrg('');
      setInviteAckOpen(false);
    });

  // Block the invite up front if the email already has a Waterlily account; only open the
  // "they need to create an account" acknowledgement once we know that's actually true.
  const handleStartInvite = async () => {
    const alreadyHasAccount = await onCheckContactExists(otherEmail.trim());
    if (alreadyHasAccount) return;

    setInviteAckOpen(true);
  };

  const selectedPendingPartners = selectedPartners.filter(
    partner => partner.approvalStatus === 'pending',
  );

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="text-foreground text-sm font-medium">
          {prospective
            ? 'Which distribution partners do you want to work with?'
            : 'Which distribution partners do you work with?'}
        </p>
        {isLoadingPartners && <p className="text-muted-foreground text-sm">Loading partners…</p>}
        {!isLoadingPartners && partners.length > PARTNER_GRID_MAX ? (
          <PartnerMultiSelect
            partners={partners}
            selectedPartnerKeys={selectedPartnerKeys}
            onTogglePartner={onTogglePartner}
            disabled={disabled}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {partners.map(partner => {
              const selected = selectedPartnerKeys.includes(partner.partnerKey);
              return (
                <button
                  key={partner.partnerKey}
                  type="button"
                  disabled={disabled}
                  onClick={() => onTogglePartner(partner.partnerKey)}
                  className={cn(
                    'hover:bg-muted relative flex flex-col items-center justify-center gap-2 rounded-md border p-4 text-center text-base font-medium transition-colors',
                    { 'border-primary bg-primary/5': selected },
                  )}
                >
                  {selected && (
                    <span className="bg-primary absolute right-2 top-2 flex size-5 items-center justify-center rounded-full text-white">
                      <Check className="size-3" />
                    </span>
                  )}
                  {partner.logoUrl && (
                    <img
                      src={partner.logoUrl}
                      alt={`${partner.displayName} logo`}
                      className="h-10 max-w-32 object-contain"
                    />
                  )}
                  <span>{partner.displayName}</span>
                  {partner.approvalStatus === 'pending' && <PendingPartnerBadge />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selectedPendingPartners.length > 0 && (
        <Alert variant="warning">
          <AlertTitle>
            {selectedPendingPartners.map(partner => partner.displayName).join(', ')} is pending
            Waterlily review.
          </AlertTitle>
          <AlertDescription>
            You can still share clients with the people you've invited there — once our team
            approves it, it becomes visible to everyone.
          </AlertDescription>
        </Alert>
      )}

      {hasPartnerSelection && (
        <div className="space-y-4">
          <p className="text-foreground text-sm font-medium">
            {prospective
              ? 'Which team members do you want to work with?'
              : 'Which team members do you work with?'}
          </p>

          {isLoadingContacts && <p className="text-muted-foreground text-sm">Loading contacts…</p>}

          {!isLoadingContacts && contacts.length === 0 && (
            <p className="text-muted-foreground text-sm">
              No contacts are available for the selected partner(s) yet.
              {!hideInvite && ' Use the option below to request one.'}
            </p>
          )}

          {contacts.length > 0 && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
                <Input
                  value={contactSearch}
                  disabled={disabled}
                  placeholder="Search by name or email…"
                  onChange={event => setContactSearch(event.target.value)}
                  className="pl-10"
                />
              </div>

              {filteredContacts.length > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    {selectedContactIds.length} selected
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled}
                    onClick={() =>
                      onToggleContacts(
                        filteredContacts.map(contact => contact.advisorId),
                        !allFilteredSelected,
                      )
                    }
                  >
                    {allFilteredSelected ? 'Deselect all' : 'Select all'}
                  </Button>
                </div>
              )}

              <ScrollArea className="max-h-120 overflow-y-auto rounded-xl border">
                <div className="space-y-2 p-2">
                  {filteredContacts.length === 0 ? (
                    <p className="text-muted-foreground py-6 text-center text-sm">
                      No team members match “{contactSearch}”.
                    </p>
                  ) : (
                    orderedContacts.map(contact => {
                      const checked = selectedContactIds.includes(contact.advisorId);
                      return (
                        <div
                          key={contact.advisorId}
                          className={cn(
                            'hover:bg-muted flex items-start gap-2 rounded-md border p-3 transition-colors',
                            { 'border-primary': checked },
                          )}
                        >
                          <Label
                            htmlFor={`${idPrefix}contact-${contact.advisorId}`}
                            className="flex min-w-0 flex-1 cursor-pointer items-start gap-3"
                          >
                            <Checkbox
                              id={`${idPrefix}contact-${contact.advisorId}`}
                              disabled={disabled}
                              checked={checked}
                              onCheckedChange={value =>
                                onToggleContact(contact.advisorId, value === true)
                              }
                              className="mt-0.5"
                            />
                            <span className="flex min-w-0 flex-col gap-2">
                              <span className="flex min-w-0 flex-col">
                                <span className="text-foreground text-base font-medium leading-tight [overflow-wrap:anywhere]">
                                  {contactDisplayName(contact)}
                                </span>
                                <span className="text-muted-foreground text-sm [overflow-wrap:anywhere]">
                                  {contact.advisorEmail}
                                  {contact.organizationName ? ` · ${contact.organizationName}` : ''}
                                </span>
                              </span>
                              {contact.isConnected && (
                                <span className="flex flex-wrap items-center gap-1.5">
                                  {edjMode ? (
                                    <>
                                      <Badge variant="success">Connected</Badge>
                                      {contact.isSharing && (
                                        <Badge variant="success">All future clients shared</Badge>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {showLegacyShare && (
                                        <Badge
                                          variant={contact.legacyShared ? 'success' : 'warning'}
                                        >
                                          {contact.legacyShared
                                            ? 'Past clients shared'
                                            : 'Past clients not shared'}
                                        </Badge>
                                      )}
                                      <Badge variant={contact.isSharing ? 'success' : 'warning'}>
                                        {contact.isSharing
                                          ? 'New clients shared automatically'
                                          : 'New clients not shared automatically'}
                                      </Badge>
                                    </>
                                  )}
                                </span>
                              )}
                            </span>
                          </Label>
                          {contact.isConnected && (
                            <div className="flex shrink-0 items-center">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="icon-sm"
                                    disabled={
                                      disabled || isSettingSharing || isSharingContactLegacy
                                    }
                                    aria-label={`Actions for ${contactDisplayName(contact)}`}
                                  >
                                    <MoreVertical />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {!edjMode ? (
                                    <DropdownMenuItem
                                      disabled={disabled || isSettingSharing}
                                      onSelect={() =>
                                        onSetContactSharing(contact, !contact.isSharing)
                                      }
                                    >
                                      <Share2 className="mr-2 size-4" />
                                      {contact.isSharing
                                        ? 'Stop sharing future clients'
                                        : 'Share all future clients'}
                                    </DropdownMenuItem>
                                  ) : (
                                    // EDJ manages sharing per-client; the only auto-share state that
                                    // can exist is the "all future clients" election, which can be
                                    // turned off here (turning it back on is not offered).
                                    contact.isSharing && (
                                      <DropdownMenuItem
                                        disabled={disabled || isSettingSharing}
                                        onSelect={() => onSetContactSharing(contact, false)}
                                      >
                                        <Share2 className="mr-2 size-4" />
                                        Stop sharing future clients
                                      </DropdownMenuItem>
                                    )
                                  )}
                                  {showLegacyShare && (
                                    <DropdownMenuItem
                                      disabled={disabled || isSharingContactLegacy}
                                      onSelect={() =>
                                        onShareContactLegacy(contact, !contact.legacyShared)
                                      }
                                    >
                                      <Clock className="mr-2 size-4" />
                                      {contact.legacyShared
                                        ? 'Stop sharing earlier clients'
                                        : 'Share earlier clients'}
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    disabled={disabled || isSettingSharing}
                                    onSelect={() => onRequestDisconnect(contact)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="mr-2 size-4" />
                                    Remove connection
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>

              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={onConnect}
                  disabled={disabled || isConnecting || !hasNewContactSelection}
                >
                  {isConnecting ? 'Connecting…' : 'Connect selected'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!hideInvite && (
        <Card className="gap-4 border p-6 shadow-none">
          <div className="space-y-1">
            <p className="text-foreground text-primary-dark text-lg font-bold">
              Don't see who you work with?
            </p>
            <p className="text-muted-foreground text-sm">
              Enter their name and work email and we'll invite them to join Waterlily as your
              distribution partner. If their firm isn't listed yet, we'll add it for review.
            </p>
          </div>

          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor={`${idPrefix}invite-name`}>Contact name</Label>
                <Input
                  id={`${idPrefix}invite-name`}
                  value={otherName}
                  disabled={disabled}
                  placeholder="Jane Doe"
                  onChange={event => setOtherName(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`${idPrefix}invite-email`}>Work email</Label>
                <Input
                  id={`${idPrefix}invite-email`}
                  type="email"
                  value={otherEmail}
                  disabled={disabled}
                  placeholder="jane@firm.com"
                  onChange={event => setOtherEmail(event.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}invite-org`}>
                Partner organization name{' '}
                <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id={`${idPrefix}invite-org`}
                value={otherOrg}
                disabled={disabled}
                placeholder="Only needed if their firm isn't listed yet"
                onChange={event => setOtherOrg(event.target.value)}
              />
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleStartInvite}
                disabled={disabled || isRequestingOther || isCheckingContact || !canInvite}
                className="w-full sm:w-auto"
              >
                {isCheckingContact ? 'Checking…' : isRequestingOther ? 'Sending…' : 'Send invite'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {!hideInvite && (
        <PartnerInviteAcknowledgeDialog
          open={inviteAckOpen}
          inviteeName={otherName}
          isSending={isRequestingOther}
          edjMode={edjMode}
          onConfirm={sendInvite}
          onCancel={() => setInviteAckOpen(false)}
        />
      )}
    </div>
  );
}
